import WisdomNavbar from "components/navbars/WisdomNabvar";
import { useAuth } from "context/AuthContext";
import { useLanguage } from "context/LanguageContext";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { updateTeacherSubject } from "services/admins";
import {
    fetchWisdomRelatedSchools,
    fetchWisdomRelatedTeachers,
    fetchWisdomSubjects
} from "services/wisdom/wisdomData";

const WisdomAdminTeacherSubjectManage = () => {
    const { userInfo } = useAuth();
    const { language } = useLanguage();
    const [schools, setSchools] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [selectedSchool, setSelectedSchool] = useState("");
    const [selectedTeacher, setSelectedTeacher] = useState("");
    const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);
    const [assignedSubjectIds, setAssignedSubjectIds] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const isMultiSchoolAdmin = userInfo?.organization_id === 13;
    const effectiveSchoolId = isMultiSchoolAdmin ? selectedSchool : userInfo?.organization_id;

    useEffect(() => {
        const loadData = async () => {
            const [schoolResponse, teacherResponse, subjectResponse] = await Promise.all([
                fetchWisdomRelatedSchools(),
                fetchWisdomRelatedTeachers(),
                fetchWisdomSubjects()
            ]);
            setSchools(schoolResponse);
            setTeachers(teacherResponse);
            setSubjects(subjectResponse);
        };

        loadData();
    }, []);

    useEffect(() => {
        if (!selectedTeacher) {
            setAssignedSubjectIds([]);
            setSelectedSubjectIds([]);
            return;
        }

        const teacher = teachers.find((t) => Number(t.id) === Number(selectedTeacher));
        const ids = (teacher?.subjects ?? [])
            .map((s) => (s?.id != null ? Number(s.id) : null))
            .filter((id) => id != null);
        setAssignedSubjectIds(ids);
        setSelectedSubjectIds(ids);
    }, [selectedTeacher, teachers]);

    const filteredTeachers = useMemo(
        () =>
            teachers.filter((t) =>
                effectiveSchoolId === "" || effectiveSchoolId == null
                    ? true
                    : t?.employee?.organization_id === Number(effectiveSchoolId)
            ),
        [teachers, effectiveSchoolId]
    );

    const filteredSubjects = useMemo(
        () =>
            subjects.filter((s) =>
                effectiveSchoolId === "" || effectiveSchoolId == null
                    ? true
                    : s?.organizations?.some((org) => org.id === Number(effectiveSchoolId))
            ),
        [subjects, effectiveSchoolId]
    );

    const isSubjectIdSelected = (subjectId) =>
        selectedSubjectIds.some((id) => Number(id) === Number(subjectId));

    const isSubjectIdAssigned = (subjectId) =>
        assignedSubjectIds.some((id) => Number(id) === Number(subjectId));

    const allFilteredSelected =
        filteredSubjects.length > 0 &&
        filteredSubjects.every((s) => isSubjectIdSelected(s.id));

    const toggleSubject = (subjectId) => {
        const normalizedId = Number(subjectId);
        setSelectedSubjectIds((prev) =>
            prev.some((id) => Number(id) === normalizedId)
                ? prev.filter((id) => Number(id) !== normalizedId)
                : [...prev, normalizedId]
        );
    };

    const toggleSelectAll = (checked) => {
        if (checked) {
            const idsToAdd = filteredSubjects.map((s) => Number(s.id));
            setSelectedSubjectIds((prev) => Array.from(new Set([...prev, ...idsToAdd])));
        } else {
            const idsToRemove = new Set(filteredSubjects.map((s) => Number(s.id)));
            setSelectedSubjectIds((prev) => prev.filter((id) => !idsToRemove.has(Number(id))));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setSubmitting(true);
            const subjectIds = selectedSubjectIds.map((id) => Number(id));
            await updateTeacherSubject({
                teacher_id: Number(selectedTeacher),
                subject_ids: subjectIds,
            });
            toast.success(language ? "Subjects updated successfully" : "تم تحديث المواد بنجاح");
            // Keep the local teacher record in sync so re-selecting shows the new assignment
            setTeachers((prev) =>
                prev.map((t) =>
                    Number(t.id) === Number(selectedTeacher)
                        ? { ...t, subjects: subjects.filter((s) => subjectIds.includes(Number(s.id))) }
                        : t
                )
            );
        } catch (error) {
            toast.error(language ? "Failed to update subjects" : "فشل تحديث المواد");
            console.error(error)
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <WisdomNavbar />
            <div className="max-w-xl mx-auto px-4 py-8" dir={language ? "ltr" : "rtl"}>
                <div className="bg-white rounded-2xl shadow p-6">
                    <h1 className="text-2xl font-bold text-gray-800 mb-6">
                        {language ? "Manage Teacher Subjects" : "إدارة مواد المعلم"}
                    </h1>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* School */}
                        {isMultiSchoolAdmin && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {language ? "School" : "مدرسة"}
                                </label>
                                <select
                                    value={selectedSchool}
                                    onChange={(e) => {
                                        setSelectedSchool(e.target.value);
                                        setSelectedTeacher("");
                                    }}
                                    className="w-full border rounded-xl px-3 py-2 bg-white focus:ring-2 focus:ring-watomsBlue"
                                >
                                    <option value="">
                                        {language ? "All schools" : "كل المدارس"}
                                    </option>
                                    {schools.map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Teacher */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {language ? "Teacher" : "معلم"}
                            </label>
                            <select
                                value={selectedTeacher}
                                onChange={(e) => setSelectedTeacher(e.target.value)}
                                className="w-full border rounded-xl px-3 py-2 bg-white focus:ring-2 focus:ring-watomsBlue"
                                required
                            >
                                <option value="" disabled>
                                    {language ? "Select teacher" : "اختر معلم"}
                                </option>
                                {filteredTeachers.map((teacher) => (
                                    <option key={teacher.id} value={teacher.id}>
                                        {teacher?.employee?.first_name} {teacher?.employee?.middle_name} {teacher?.employee?.last_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Subjects */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    {language ? "Subjects" : "المواد"}
                                </label>

                                <label className="flex items-center gap-2 text-sm text-gray-700">
                                    <input
                                        type="checkbox"
                                        checked={allFilteredSelected}
                                        onChange={(e) => toggleSelectAll(e.target.checked)}
                                        disabled={filteredSubjects.length === 0 || !selectedTeacher}
                                    />
                                    {language ? "Select all" : "اختيار الكل"}
                                </label>
                            </div>

                            <div className="border rounded-xl p-3 max-h-64 overflow-y-auto space-y-2">
                                {filteredSubjects.length === 0 && (
                                    <p className="text-sm text-gray-500">
                                        {language ? "No subjects found" : "لا يوجد مواد"}
                                    </p>
                                )}
                                {filteredSubjects.map((s) => {
                                    const isAssigned = isSubjectIdAssigned(s.id);

                                    return (
                                        <label
                                            key={s.id}
                                            className={`flex items-center gap-2 text-sm text-gray-700 rounded-lg px-2 py-1 ${
                                                isAssigned ? "bg-green-50 border border-green-200" : ""
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isSubjectIdSelected(s.id)}
                                                onChange={() => toggleSubject(s.id)}
                                                disabled={!selectedTeacher}
                                            />
                                            <span className="flex-1">{s.name}</span>
                                            {isAssigned && (
                                                <span className="text-xs text-green-700">
                                                    {language ? "Assigned" : "معين"}
                                                </span>
                                            )}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            className="w-full py-2 rounded-xl bg-watomsBlue text-white hover:opacity-90 transition disabled:opacity-50"
                            disabled={!selectedTeacher || submitting}
                        >
                            {submitting
                                ? (language ? "Saving..." : "جاري الحفظ...")
                                : (language ? "Save Subjects" : "حفظ المواد")}
                        </button>
                    </form>
                </div>
            </div>
        </>
    )
};

export default WisdomAdminTeacherSubjectManage;
